import React from "react";
import { DialogButton, DialogConfig, createDialogConfig } from "common/component/SequentialDialog";
import { formatDateStr, formatDateWithValidate } from "common/util/date";
import { 
  AuthorizationInfo, 
  ClientReportReportStatus, 
  ClientReportReportStatusMap 
} from "../../domain";
import { createButton, getReportTypeDisplayName, getArticle } from "./utils";

const createContent = (
  auth: AuthorizationInfo | undefined,
  selectedReportType: string
): React.ReactNode => {
  const existingReport = auth?.latestSubmittedClientReportByReportType?.find(
    item => item.reportType === selectedReportType
  );
  
  if (!existingReport) return null;

  const reportTypeDisplay = getReportTypeDisplayName(existingReport.reportType);
  const article = getArticle(reportTypeDisplay);
  const reportStatus = ClientReportReportStatusMap.get(existingReport.reportStatus);
  const submittedDate = existingReport.submittedAt ? formatDateWithValidate(new Date(existingReport?.submittedAt)) : 'N/A';
  const approvedDate = existingReport.authorizedDate ? formatDateStr(existingReport.authorizedDate) : 'N/A';

  // Review In Progress or Returned to Provider for Corrections or Pending Review of Provider Revisions
  if ([
    ClientReportReportStatus.REVIEW_IN_PROGRESS,
    ClientReportReportStatus.RETURNED_TO_PROVIDER_FOR_CORRECTIONS,
    ClientReportReportStatus.PENDING_REVIEW_OF_PROVIDER_REVISIONS,
    ClientReportReportStatus.FUNDER_REVISION_REQUESTED_ADMIN_REVIEW
  ].includes(reportStatus as ClientReportReportStatus)) {
    return (
      <div>
        {article} {reportTypeDisplay} was submitted on {submittedDate}. Submitting a new {reportTypeDisplay} will replace the previously submitted report.
      </div>
    );
  }

  // Report Authorized or Submitted to Funder
  if ([
    ClientReportReportStatus.REPORT_AUTHORIZED,
    ClientReportReportStatus.SUBMITTED_TO_FUNDER
  ].includes(reportStatus as ClientReportReportStatus)) {
    return (
      <div>
        {article} {reportTypeDisplay} was submitted on {submittedDate} and approved on {approvedDate}. 
        Another {reportTypeDisplay} cannot be submitted from this authorization, unless the Report Status is "Funder Revision Requested".
      </div>
    );
  }
  
  // Funder Revision Requested or Funder Revision Requested Admin Review
  if ([
    ClientReportReportStatus.FUNDER_REVISION_REQUESTED,
    ClientReportReportStatus.FUNDER_REVISION_REQUESTED_ADMIN_REVIEW
  ].includes(reportStatus as ClientReportReportStatus)) {
    return (
      <div>
        {article} {reportTypeDisplay} was submitted on {submittedDate} and approved on {approvedDate}. 
        Click on "Submit" if you are submitting an updated report due to "Funder Revisions Requested".
      </div>
    );
  }

  // Default fallback
  return (
    <div>
      {article} {reportTypeDisplay} was submitted on {submittedDate}. Submitting a new {reportTypeDisplay} will replace the previously submitted report.
    </div>
  );
};

const createButtons = (
  auth: AuthorizationInfo | undefined,
  selectedReportType: string
): DialogButton[] => {
  const existingReport = auth?.latestSubmittedClientReportByReportType?.find(
    item => item.reportType === selectedReportType
  );
  
  if (!existingReport) return [];

  const reportStatus = ClientReportReportStatusMap.get(existingReport.reportStatus);

  // Review in Progress or Returned to Provider for Corrections - Show Submit button
  if ([
    ClientReportReportStatus.REVIEW_IN_PROGRESS,
    ClientReportReportStatus.RETURNED_TO_PROVIDER_FOR_CORRECTIONS,
    ClientReportReportStatus.PENDING_REVIEW_OF_PROVIDER_REVISIONS,
    ClientReportReportStatus.FUNDER_REVISION_REQUESTED,
    ClientReportReportStatus.FUNDER_REVISION_REQUESTED_ADMIN_REVIEW
  ].includes(reportStatus as ClientReportReportStatus)) {
    return [
      createButton('Submit', { action: 'submit', updateExistingReport: true }, 'green', '120px')
    ];
  }

  // Approved OR Submitted to Funder - Show only OK (closes modal)
  if ([
    ClientReportReportStatus.REPORT_AUTHORIZED,
    ClientReportReportStatus.SUBMITTED_TO_FUNDER
  ].includes(reportStatus as ClientReportReportStatus)) {
    return [
      createButton('OK', { action: 'cancel' }, 'green', '120px')
    ];
  }

  // Default fallback
  return [
    createButton('Submit', { action: 'submit', updateExistingReport: true }, 'green', '120px')
  ];
};

const shouldNotShowCancelButton = (
  auth: AuthorizationInfo | undefined,
  selectedReportType: string
): boolean => {
  const existingReport = auth?.latestSubmittedClientReportByReportType?.find(
    item => item.reportType === selectedReportType
  );
  
  if (!existingReport) return false;

  const reportStatus = ClientReportReportStatusMap.get(existingReport.reportStatus);
  
  return [
    ClientReportReportStatus.REPORT_AUTHORIZED,
    ClientReportReportStatus.SUBMITTED_TO_FUNDER
  ].includes(reportStatus as ClientReportReportStatus);
};

export const createReportReplacementDialogConfig = (
  auth: AuthorizationInfo | undefined,
  selectedReportType: string,
  shouldShow: boolean
): DialogConfig => {
  return createDialogConfig(
    'report-replacement-warning',
    'Confirm!',
    createContent(auth, selectedReportType),
    createButtons(auth, selectedReportType),
    shouldShow,
    !shouldNotShowCancelButton(auth, selectedReportType)
  );
};
